import { useCallback, useEffect, useState } from 'react';
import { fetchStoryRow } from '@/modules/databaseService/scenes';
import type { ChosenVisibility } from '@/infrastructure/api/v1/mappers/stories';

/**
 * The visibility question's answer: unknown until every story reads, then unlisted only when every one
 * already is, so a new version keeps the creator's earlier answer. Once picked, the pick wins.
 * Unknown means nothing is sent and the server keeps what it has: a failed read must not make a story public.
 */
export function useStoryVisibilityChoice(storyIds: readonly string[], enabled: boolean) {
  const [loaded, setLoaded] = useState<{ key: string; visibility: ChosenVisibility } | null>(null);
  const [picked, setPicked] = useState<ChosenVisibility | null>(null);
  const key = storyIds.join(',');
  const current = loaded?.key === key ? loaded.visibility : null;

  useEffect(() => {
    if (!enabled || !key) return;
    let cancelled = false;
    void Promise.all(key.split(',').map(fetchStoryRow)).then((results) => {
      if (cancelled || results.some((r) => r.error || !r.data)) return;
      const allUnlisted = results.every((r) => r.data?.visibility === 'unlisted');
      setLoaded({ key, visibility: allUnlisted ? 'unlisted' : 'public' });
    });
    return () => {
      cancelled = true;
    };
  }, [key, enabled]);

  const reset = useCallback(() => setPicked(null), []);

  return { visibility: picked ?? current ?? undefined, setVisibility: setPicked, reset };
}
